import { useNavigate, useParams, useOutletContext } from "react-router-dom";
import { useState } from "react";
import { Button } from "../components/atoms";
import { createEmploymentType, updateEmploymentType } from "../api/employmentTypes";
import {
    EMPTY_EMPLOYMENT_TYPE_FORM_STATE,
    type EmploymentTypeFormOutletContext,
    type EmploymentTypeFormState,
} from "../components/EmploymentTypeEditLayout";

/**
 * タグの型定義
 */
interface Tag {
    id: string;
    name: string;
    color: "orange" | "green" | "red";
}

/** タグの色定義 */
const TAG_COLORS = {
    orange: "#FFDCC7",
    green: "#A1D1A1",
    red: "#D1A1A1",
} as const;

/** デフォルトの紐づけ済みタグID（新規作成時） */
const DEFAULT_SELECTED_TAG_IDS: string[] = ["1", "3", "5"];

/** タグ一覧（サンプルデータ）- 雇用形態向けタグマスター */
const ALL_TAGS: Tag[] = [
    { id: "1", name: "シフト制", color: "orange" },
    { id: "2", name: "固定勤務", color: "orange" },
    { id: "3", name: "フルタイム", color: "green" },
    { id: "4", name: "短時間勤務", color: "green" },
    { id: "5", name: "残業あり", color: "red" },
    { id: "6", name: "残業なし", color: "red" },
    { id: "7", name: "夜勤あり", color: "orange" },
    { id: "8", name: "土日祝休み", color: "green" },
];

/** タグチップコンポーネント（表示のみ） */
const TagChip = ({ tag }: { tag: Tag }) => (
    <span
        className="px-6 py-1.5 rounded-lg text-sm text-gray-700"
        style={{ backgroundColor: TAG_COLORS[tag.color] }}
    >
        {tag.name}
    </span>
);

/**
 * 雇用形態 - タグ設定ページ（Step 3・最終）
 */
const EmploymentTypeTagFormPage = () => {
    const navigate = useNavigate();
    const {
        setPageError,
        setInvalidFields,
        pendingFormState,
    } = useOutletContext<EmploymentTypeFormOutletContext>();
    const { id } = useParams<{ id?: string }>();
    const isEdit = !!id;
    const employmentTypeId = id ? parseInt(id, 10) : null;

    const [saving, setSaving] = useState(false);

    // 選択済みタグ（Coming Soon: 現状固定）
    const selectedTagIds = DEFAULT_SELECTED_TAG_IDS;
    const selectedTags = ALL_TAGS.filter((tag) => selectedTagIds.includes(tag.id));
    const displayTags = ALL_TAGS.filter((tag) => !selectedTagIds.includes(tag.id));
    const orangeTags = displayTags.filter((tag) => tag.color === "orange");
    const greenTags = displayTags.filter((tag) => tag.color === "green");
    const redTags = displayTags.filter((tag) => tag.color === "red");

    const handleBack = () => {
        navigate(isEdit ? `/tenant/employment-type/${id}/work-rules` : "/tenant/employment-type/new/work-rules");
    };

    const handleCancel = () => {
        navigate("/tenant/employment-type");
    };

    /**
     * 必須項目チェック。空フィールドがあれば setInvalidFields + setPageError。
     * @returns 全項目入力済みなら true
     */
    const validateRequired = (state: EmploymentTypeFormState): boolean => {
        const missing: { key: string; label: string }[] = [];
        if (!state.code.trim()) missing.push({ key: "code", label: "雇用形態コード" });
        if (!state.name.trim()) missing.push({ key: "name", label: "雇用形態名" });
        if (missing.length > 0) {
            setInvalidFields(missing.map((f) => f.key));
            setPageError(`必須項目が入力されていません: ${missing.map((f) => f.label).join(", ")}`);
            return false;
        }
        return true;
    };

    const handleComplete = async () => {
        // 編集時で state 無し（サイドバー直接遷移等）: 基本情報の変更が無いので一覧に戻るだけ
        if (isEdit && !pendingFormState) {
            navigate("/tenant/employment-type");
            return;
        }
        // 新規登録で未入力のまま来た場合は全フィールド空として扱い、通常の validation 経路に流す
        const stateForValidation: EmploymentTypeFormState =
            pendingFormState ?? EMPTY_EMPLOYMENT_TYPE_FORM_STATE;
        if (!validateRequired(stateForValidation)) return;

        setInvalidFields([]);
        setSaving(true);
        setPageError(null);
        try {
            if (isEdit && employmentTypeId !== null) {
                await updateEmploymentType(employmentTypeId, {
                    code: stateForValidation.code,
                    name: stateForValidation.name,
                });
            } else {
                await createEmploymentType({
                    code: stateForValidation.code,
                    name: stateForValidation.name,
                });
            }
            navigate("/tenant/employment-type");
        } catch (err) {
            console.error("Failed to save employment type:", err);
            setPageError(err instanceof Error ? err.message : (isEdit ? "更新に失敗しました" : "登録に失敗しました"));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="flex-1 flex bg-white rounded-lg overflow-hidden">
            {/* メインコンテンツ */}
            <div className="flex-1 flex flex-col">
                <div className="flex-1 p-6 relative">
                    <div className="space-y-8">
                        {/* 紐づけ済みのタグ */}
                        <section>
                            <div className="flex gap-8">
                                <label className="text-sm text-gray-700 w-32 flex-shrink-0 pt-2">
                                    紐づけ済みのタグ
                                </label>
                                <div className="flex-1 border border-gray-200 rounded-lg p-4 min-h-24">
                                    <div className="flex flex-wrap gap-3">
                                        {selectedTags.map((tag) => (
                                            <TagChip key={tag.id} tag={tag} />
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </section>

                        {/* タグ一覧 */}
                        <section>
                            <div className="flex gap-8">
                                <label className="text-sm text-gray-700 w-32 flex-shrink-0 pt-0.5">
                                    タグ一覧
                                </label>
                                <div className="space-y-3">
                                    <div className="flex gap-3">
                                        {orangeTags[0] && <TagChip tag={orangeTags[0]} />}
                                        {greenTags[0] && <TagChip tag={greenTags[0]} />}
                                        {redTags[0] && <TagChip tag={redTags[0]} />}
                                    </div>
                                    <div className="flex gap-3">
                                        {orangeTags[1] && <TagChip tag={orangeTags[1]} />}
                                        {greenTags[1] && <TagChip tag={greenTags[1]} />}
                                        {redTags[1] && <TagChip tag={redTags[1]} />}
                                    </div>
                                    <div className="flex gap-3">
                                        {orangeTags[2] && <TagChip tag={orangeTags[2]} />}
                                    </div>
                                </div>
                            </div>
                        </section>
                    </div>

                    {/* Coming Soon オーバーレイ */}
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center z-10">
                        <span className="text-white text-3xl font-bold tracking-widest">Coming Soon</span>
                    </div>
                </div>

                {/* フッターボタン */}
                <div className="flex justify-end gap-4 px-6 py-4">
                    <Button variant="primary" size="md" onClick={handleBack} className="px-8">
                        2.契約情報の設定に戻る
                    </Button>
                    <Button variant="outline" size="md" onClick={handleCancel} className="px-8">
                        キャンセル
                    </Button>
                    <Button variant="primary" size="md" onClick={handleComplete} disabled={saving} className="px-8">
                        {saving ? (isEdit ? "更新中..." : "登録中...") : (isEdit ? "設定を完了する" : "登録する")}
                    </Button>
                </div>
            </div>

            {/* 区切り線（右端） */}
            <div className="w-px bg-gray-200 my-4" />
        </div>
    );
};

export default EmploymentTypeTagFormPage;
